import { gql, useMutation, useQuery } from '@apollo/client'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import PauseCircleIcon from '@mui/icons-material/PauseCircle'
import RefreshIcon from '@mui/icons-material/Refresh'
import WarningIcon from '@mui/icons-material/Warning'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  Link as MuiLink,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { type ReactNode, useEffect, useMemo, useState } from 'react'
import { Title, useLocale, useNotify, useTranslate } from 'react-admin'
import { Link } from 'react-router-dom'
import { nautixMissing } from '~/helpers'
import { BoatvertizerStats } from './BoatvertizerStats'

/* ------------------------------------------------------------------ data */

const FEED_STATUS = gql`
  query FeedStatus($since: timestamptz!) {
    settings: nautix_settings_by_pk(id: 1) {
      id
      feed_enabled
      account_name
      office_name
      office_address
      office_postcode
      office_town
      office_country
      office_email
      office_phone
      strip_contact_details
      shrink_guard_percent
    }
    runs: nautix_runs(order_by: { started_at: desc }, limit: 15) {
      id
      started_at
      status
      advert_count
      skipped
      message
    }
    lastPublished: nautix_runs(
      where: { status: { _eq: "published" } }
      order_by: { started_at: desc }
      limit: 1
    ) {
      id
      started_at
      advert_count
    }
    fetches: nautix_fetches(order_by: { fetched_at: desc }, limit: 30) {
      id
      fetched_at
      client_ip
      user_agent
      method
      status
      bytes
    }
    recentFetches: nautix_fetches_aggregate(where: { fetched_at: { _gte: $since } }) {
      aggregate {
        count
      }
    }
    released: boats(where: { publish_nautix: { _eq: true } }, order_by: { name: asc }) {
      id
      name
      manufacturer
      model
      boat_type
      motorboat_type
      asking_price
      location_country
    }
    notReleased: boats_aggregate(where: { publish_nautix: { _eq: false } }) {
      aggregate {
        count
      }
    }
  }
`

const GENERATE = gql`
  mutation GenerateFeed($force: Boolean) {
    nautix_generate(force: $force) {
      status
      advert_count
      message
      run_id
    }
  }
`

const SAVE_SETTINGS = gql`
  mutation SaveFeedSettings($set: nautix_settings_set_input!) {
    update_nautix_settings_by_pk(pk_columns: { id: 1 }, _set: $set) {
      id
    }
  }
`

/** What the generator requires of the settings (REQUIRED_SETTINGS in nautix/lib/render.mjs). */
const REQUIRED_SETTINGS = [
  'account_name',
  'office_name',
  'office_address',
  'office_town',
  'office_country',
  'office_email',
] as const

const SETTING_FIELDS = [
  'account_name',
  'office_name',
  'office_address',
  'office_postcode',
  'office_town',
  'office_country',
  'office_email',
  'office_phone',
] as const

type Settings = Record<string, any>

/**
 * Where the portals fetch the feed: feed.<domain> next to cms.<domain>, as
 * Caddy serves it; the development stack publishes it on port 8090.
 */
const feedUrl = () => {
  const { hostname } = window.location
  return hostname.startsWith('cms.')
    ? `https://feed.${hostname.slice(4)}/nautix.xml`
    : 'http://localhost:8090/nautix.xml'
}

/* --------------------------------------------------------------- helpers */

const useRelativeTime = () => {
  const locale = useLocale()
  return useMemo(() => {
    const format = new Intl.RelativeTimeFormat(locale, { numeric: 'auto' })
    const steps: [Intl.RelativeTimeFormatUnit, number][] = [
      ['day', 86400],
      ['hour', 3600],
      ['minute', 60],
    ]
    return (iso: string) => {
      const seconds = (new Date(iso).getTime() - Date.now()) / 1000
      for (const [unit, size] of steps) {
        if (Math.abs(seconds) >= size) return format.format(Math.round(seconds / size), unit)
      }
      return format.format(0, 'minute')
    }
  }, [locale])
}

const useDateTime = () => {
  const locale = useLocale()
  return useMemo(() => {
    const format = new Intl.DateTimeFormat(locale, { dateStyle: 'short', timeStyle: 'short' })
    return (iso: string) => format.format(new Date(iso))
  }, [locale])
}

/** "boat24 crawler (203.0.113.7)" rather than a 200-character user agent. */
const describeClient = (fetch: { user_agent?: string | null; client_ip?: string | null }) => {
  const agent = fetch.user_agent ?? ''
  const known = /boat24|boatvertizer|nautix/i.exec(agent)?.[0]
  const name = known ?? (agent.length > 48 ? `${agent.slice(0, 48)}…` : agent || '—')
  const ip = (fetch.client_ip ?? '').replace(/^::ffff:/, '')
  return ip ? `${name} (${ip})` : name
}

/* --------------------------------------------------------------- pieces */

type Tone = 'good' | 'warning' | 'off'

const TONE_ICON: Record<Tone, ReactNode> = {
  good: <CheckCircleIcon fontSize='small' color='success' />,
  warning: <WarningIcon fontSize='small' color='warning' />,
  off: <PauseCircleIcon fontSize='small' color='disabled' />,
}

/** A headline number or state, with what it means underneath. */
const StatTile = ({
  label,
  value,
  sub,
  tone,
  testId,
}: {
  label: string
  value: ReactNode
  sub?: ReactNode
  tone?: Tone
  testId?: string
}) => (
  <Card variant='outlined' sx={{ height: '100%' }} data-testid={testId}>
    <CardContent>
      <Typography variant='body2' color='text.secondary'>
        {label}
      </Typography>
      <Stack direction='row' spacing={1} sx={{ my: 0.5, alignItems: 'center' }}>
        {tone && TONE_ICON[tone]}
        <Typography variant='h4' component='div'>
          {value}
        </Typography>
      </Stack>
      {sub && (
        <Typography variant='body2' color='text.secondary'>
          {sub}
        </Typography>
      )}
    </CardContent>
  </Card>
)

const Section = ({
  title,
  children,
  action,
}: {
  title: string
  children: ReactNode
  action?: ReactNode
}) => (
  <Card variant='outlined'>
    <CardContent>
      <Stack direction='row' sx={{ mb: 1, justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant='h6'>{title}</Typography>
        {action}
      </Stack>
      {children}
    </CardContent>
  </Card>
)

/**
 * Why a run published nothing, in words for the office. The generator's own
 * message (English, technical) stays available beneath it.
 */
const reasonKey = (message?: string | null) => {
  const text = message ?? ''
  if (/feed_enabled is false/.test(text)) return 'feed.reasons.disabled'
  if (/settings are incomplete|nautix_settings row is missing/.test(text))
    return 'feed.reasons.settings'
  if (/shrink guard/.test(text)) return 'feed.reasons.shrink'
  if (/does not validate/.test(text)) return 'feed.reasons.invalid'
  return 'feed.reasons.other'
}

const warningCount = (message?: string | null) =>
  Number(/(\d+) warning\(s\)/.exec(message ?? '')?.[1] ?? 0)

const RUN_TONE: Record<string, 'success' | 'warning' | 'error'> = {
  published: 'success',
  rejected: 'warning',
  failed: 'error',
}

/* ---------------------------------------------------------------- page */

export const FeedStatus = () => {
  const translate = useTranslate()
  const notify = useNotify()
  const relative = useRelativeTime()
  const dateTime = useDateTime()
  const since = useMemo(() => new Date(Date.now() - 30 * 86400 * 1000).toISOString(), [])
  const { data, loading, error, refetch } = useQuery(FEED_STATUS, {
    variables: { since },
    pollInterval: 60_000,
    fetchPolicy: 'cache-and-network',
  })
  const [generate, { loading: generating }] = useMutation(GENERATE)
  const [outcome, setOutcome] = useState<any>(null)
  const [confirmForce, setConfirmForce] = useState(false)

  if (error) return <Alert severity='error'>{error.message}</Alert>
  if (!data) return loading ? <CircularProgress sx={{ m: 4 }} /> : null

  const { settings, runs, lastPublished, fetches, released } = data
  const latest = runs[0]
  const published = lastPublished[0]
  const skippedById = new Map<number, string[]>(
    (latest?.skipped ?? []).map((s: any) => [s.boat_id, s.missing as string[]])
  )
  const heldBack = released.filter(
    (boat: any) => skippedById.has(boat.id) || nautixMissing(boat).length > 0
  ).length
  const lastFetch = fetches[0]
  const missingLabels = (keys: string[]) =>
    keys.map((key) => translate(`resources.boats.nautix.required.${key}`, { _: key })).join(', ')

  const run = async (force: boolean) => {
    setOutcome(null)
    try {
      const result = await generate({ variables: { force } })
      setOutcome(result.data?.nautix_generate)
    } catch (e: any) {
      setOutcome({ status: 'failed', message: e.message })
    }
    refetch()
  }

  const url = feedUrl()

  return (
    <Box sx={{ p: 2 }}>
      <Title title={translate('feed.title')} />
      <Typography variant='body1' color='text.secondary' sx={{ mb: 2, maxWidth: 900 }}>
        {translate('feed.intro')}
      </Typography>

      <Stack direction='row' spacing={1} sx={{ mb: 3, alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography variant='body2' color='text.secondary'>
          {translate('feed.url')}:
        </Typography>
        <MuiLink href={url} target='_blank' rel='noopener' data-testid='feed-url'>
          {url}
        </MuiLink>
        <Tooltip title={translate('feed.copy')}>
          <IconButton
            size='small'
            aria-label={translate('feed.copy')}
            onClick={() =>
              navigator.clipboard
                ?.writeText(url)
                .then(() => notify('feed.copied', { type: 'info' }))
                .catch(() => {})
            }
          >
            <ContentCopyIcon fontSize='small' />
          </IconButton>
        </Tooltip>
        <Box sx={{ flexGrow: 1 }} />
        <Button
          variant='contained'
          startIcon={generating ? <CircularProgress size={16} color='inherit' /> : <RefreshIcon />}
          disabled={generating}
          onClick={() => run(false)}
        >
          {translate(generating ? 'feed.generating' : 'feed.generate')}
        </Button>
      </Stack>

      {outcome && (
        <Alert
          severity={RUN_TONE[outcome.status] ?? 'info'}
          sx={{ mb: 3, whiteSpace: 'pre-line' }}
          data-testid='feed-outcome'
          action={
            outcome.status === 'rejected' && /shrink guard/i.test(outcome.message ?? '') ? (
              <Button color='inherit' size='small' onClick={() => setConfirmForce(true)}>
                {translate('feed.force')}
              </Button>
            ) : undefined
          }
        >
          {translate(`feed.outcome.${outcome.status}`, {
            count: outcome.advert_count ?? 0,
            reason: translate(reasonKey(outcome.message)),
          })}
          {outcome.status !== 'published' && outcome.message && (
            <Typography variant='caption' component='div' sx={{ mt: 0.5, opacity: 0.8 }}>
              {outcome.message}
            </Typography>
          )}
        </Alert>
      )}

      <Dialog open={confirmForce} onClose={() => setConfirmForce(false)}>
        <DialogContent>{translate('feed.force_confirm')}</DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmForce(false)}>{translate('feed.cancel')}</Button>
          <Button
            color='warning'
            onClick={() => {
              setConfirmForce(false)
              run(true)
            }}
          >
            {translate('feed.force')}
          </Button>
        </DialogActions>
      </Dialog>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            testId='tile-feed'
            label={translate('feed.tiles.feed')}
            value={translate(settings?.feed_enabled ? 'feed.tiles.enabled' : 'feed.tiles.disabled')}
            tone={settings?.feed_enabled ? 'good' : 'off'}
            sub={translate(
              settings?.feed_enabled ? 'feed.tiles.enabled_sub' : 'feed.tiles.disabled_sub'
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            testId='tile-adverts'
            label={translate('feed.tiles.adverts')}
            value={published?.advert_count ?? '—'}
            sub={
              published
                ? translate('feed.tiles.adverts_sub', { when: relative(published.started_at) })
                : translate('feed.tiles.never')
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            testId='tile-held'
            label={translate('feed.tiles.held_back')}
            value={heldBack}
            tone={heldBack > 0 ? 'warning' : undefined}
            sub={translate('feed.tiles.held_back_sub')}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
          <StatTile
            testId='tile-fetches'
            label={translate('feed.tiles.fetches')}
            value={data.recentFetches.aggregate.count}
            sub={
              lastFetch
                ? translate('feed.tiles.last_fetch', { when: relative(lastFetch.fetched_at) })
                : translate('feed.tiles.no_fetch')
            }
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 6 }}>
          <Section title={translate('feed.boats.title')}>
            {released.length === 0 ? (
              <Typography color='text.secondary'>{translate('feed.boats.empty')}</Typography>
            ) : (
              <Table size='small' data-testid='feed-boats'>
                <TableHead>
                  <TableRow>
                    <TableCell>{translate('feed.boats.boat')}</TableCell>
                    <TableCell>{translate('feed.boats.state')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {released.map((boat: any) => {
                    const missing = skippedById.get(boat.id) ?? nautixMissing(boat)
                    return (
                      <TableRow key={boat.id}>
                        <TableCell>
                          <MuiLink component={Link} to={`/boats/${boat.id}`}>
                            {boat.name || [boat.manufacturer, boat.model].filter(Boolean).join(' ')}
                          </MuiLink>
                        </TableCell>
                        <TableCell>
                          {missing.length === 0 ? (
                            <Chip
                              size='small'
                              color='success'
                              variant='outlined'
                              label={translate('feed.boats.in_feed')}
                            />
                          ) : (
                            <Tooltip
                              title={translate('feed.boats.missing', {
                                fields: missingLabels(missing),
                              })}
                            >
                              <Chip
                                size='small'
                                color='warning'
                                variant='outlined'
                                label={`${translate('feed.boats.held')}: ${missingLabels(missing)}`}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            )}
            {data.notReleased.aggregate.count > 0 && (
              <Typography variant='body2' color='text.secondary' sx={{ mt: 1 }}>
                {translate('feed.boats.unpublished', {
                  smart_count: data.notReleased.aggregate.count,
                })}
              </Typography>
            )}
          </Section>
        </Grid>

        <Grid size={{ xs: 12, lg: 6 }}>
          <SettingsSection settings={settings} onSaved={refetch} />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Section title={translate('feed.runs.title')}>
            {runs.length === 0 ? (
              <Typography color='text.secondary'>{translate('feed.runs.empty')}</Typography>
            ) : (
              <Table size='small' data-testid='feed-runs'>
                <TableHead>
                  <TableRow>
                    <TableCell>{translate('feed.runs.when')}</TableCell>
                    <TableCell>{translate('feed.runs.result')}</TableCell>
                    <TableCell align='right'>{translate('feed.runs.adverts')}</TableCell>
                    <TableCell>{translate('feed.runs.message')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runs.map((r: any) => (
                    <TableRow key={r.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateTime(r.started_at)}</TableCell>
                      <TableCell>
                        <Chip
                          size='small'
                          variant='outlined'
                          color={RUN_TONE[r.status] ?? 'default'}
                          label={translate(`feed.runs.${r.status}`, { _: r.status })}
                        />
                      </TableCell>
                      <TableCell align='right'>{r.advert_count ?? '—'}</TableCell>
                      <TableCell sx={{ wordBreak: 'break-word' }}>
                        {r.status === 'published'
                          ? [
                              translate('feed.runs.summary_adverts', {
                                smart_count: r.advert_count ?? 0,
                              }),
                              r.skipped?.length
                                ? translate('feed.runs.summary_skipped', {
                                    smart_count: r.skipped.length,
                                  })
                                : null,
                              warningCount(r.message)
                                ? translate('feed.runs.summary_warnings', {
                                    smart_count: warningCount(r.message),
                                  })
                                : null,
                            ]
                              .filter(Boolean)
                              .join(', ')
                          : translate(reasonKey(r.message))}
                        {(r.status !== 'published' || warningCount(r.message) > 0) && r.message && (
                          <Typography
                            variant='caption'
                            component='div'
                            color='text.secondary'
                            sx={{ whiteSpace: 'pre-line' }}
                          >
                            {r.message}
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Section>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Section title={translate('feed.fetches.title')}>
            <Typography variant='body2' color='text.secondary' sx={{ mb: 1 }}>
              {translate('feed.fetches.hint')}
            </Typography>
            {fetches.length === 0 ? (
              <Typography color='text.secondary'>{translate('feed.fetches.empty')}</Typography>
            ) : (
              <Table size='small' data-testid='feed-fetches'>
                <TableHead>
                  <TableRow>
                    <TableCell>{translate('feed.fetches.when')}</TableCell>
                    <TableCell>{translate('feed.fetches.client')}</TableCell>
                    <TableCell>{translate('feed.fetches.result')}</TableCell>
                    <TableCell align='right'>{translate('feed.fetches.size')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {fetches.map((f: any) => (
                    <TableRow key={f.id}>
                      <TableCell sx={{ whiteSpace: 'nowrap' }}>{dateTime(f.fetched_at)}</TableCell>
                      <TableCell>{describeClient(f)}</TableCell>
                      <TableCell>
                        {translate(`feed.fetches.status_${f.status}`, { _: String(f.status) })}
                        {f.method === 'HEAD' ? ' (HEAD)' : ''}
                      </TableCell>
                      <TableCell align='right'>
                        {f.bytes ? `${Math.round(f.bytes / 1024)} kB` : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Section>
        </Grid>
        <Grid size={{ xs: 12 }}>
          <BoatvertizerStats />
        </Grid>
      </Grid>
    </Box>
  )
}

/** The account block the feed carries, the kill switch and the two policies. */
const SettingsSection = ({
  settings,
  onSaved,
}: {
  settings: Settings | null
  onSaved: () => void
}) => {
  const translate = useTranslate()
  const notify = useNotify()
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState<Settings>({})
  const [save, { loading: saving }] = useMutation(SAVE_SETTINGS)

  useEffect(() => {
    if (!editing && settings) setDraft(settings)
  }, [settings, editing])

  const missing = REQUIRED_SETTINGS.filter((key) => !String(settings?.[key] ?? '').trim())
  const set = (key: string, value: unknown) => setDraft((d) => ({ ...d, [key]: value }))

  const submit = async () => {
    const { id: _id, __typename: _typename, ...fields } = draft
    for (const key of SETTING_FIELDS) {
      const value = String(fields[key] ?? '').trim()
      fields[key] = value === '' ? (key === 'account_name' ? '' : null) : value
    }
    fields.office_country = fields.office_country?.toUpperCase() ?? null
    fields.shrink_guard_percent = Math.max(
      0,
      Math.min(100, Number(fields.shrink_guard_percent) || 0)
    )
    await save({ variables: { set: fields } })
    notify('feed.settings.saved', { type: 'info' })
    setEditing(false)
    onSaved()
  }

  return (
    <Section
      title={translate('feed.settings.title')}
      action={
        editing ? (
          <Button variant='contained' size='small' disabled={saving} onClick={submit}>
            {translate('feed.settings.save')}
          </Button>
        ) : (
          <Button size='small' onClick={() => setEditing(true)}>
            {translate('feed.settings.edit')}
          </Button>
        )
      }
    >
      {missing.length > 0 && (
        <Alert severity='warning' sx={{ mb: 2 }}>
          {translate('feed.settings.incomplete', {
            fields: missing.map((key) => translate(`feed.settings.${key}`)).join(', '),
          })}
        </Alert>
      )}
      <FormControlLabel
        control={
          <Switch
            checked={!!draft.feed_enabled}
            disabled={!editing}
            onChange={(e) => set('feed_enabled', e.target.checked)}
          />
        }
        label={translate('feed.settings.feed_enabled')}
      />
      <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
        {translate('feed.settings.feed_enabled_hint')}
      </Typography>
      <Grid container spacing={2}>
        {SETTING_FIELDS.map((key) => (
          <Grid key={key} size={{ xs: 12, sm: key === 'office_address' ? 12 : 6 }}>
            <TextField
              size='small'
              fullWidth
              label={translate(`feed.settings.${key}`)}
              value={draft[key] ?? ''}
              disabled={!editing}
              required={(REQUIRED_SETTINGS as readonly string[]).includes(key)}
              onChange={(e) => set(key, e.target.value)}
            />
          </Grid>
        ))}
        <Grid size={{ xs: 12 }}>
          <FormControlLabel
            control={
              <Switch
                checked={!!draft.strip_contact_details}
                disabled={!editing}
                onChange={(e) => set('strip_contact_details', e.target.checked)}
              />
            }
            label={translate('feed.settings.strip_contact_details')}
          />
          <Typography variant='body2' color='text.secondary'>
            {translate('feed.settings.strip_contact_details_hint')}
          </Typography>
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <TextField
            size='small'
            type='number'
            fullWidth
            label={translate('feed.settings.shrink_guard_percent')}
            helperText={translate('feed.settings.shrink_guard_percent_hint')}
            value={draft.shrink_guard_percent ?? 20}
            disabled={!editing}
            slotProps={{ htmlInput: { min: 0, max: 100 } }}
            onChange={(e) => set('shrink_guard_percent', e.target.value)}
          />
        </Grid>
      </Grid>
    </Section>
  )
}

export default FeedStatus
